import React, {Component, ReactElement} from 'react'

import NatDisasterRepo from './repository/NatDisasterRepo'
import {DisasterInfo} from './repository/NatSearchDisasterInfoListResponse'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {NatGetCondominium} from '../condominium/repository/NatGetCondominium'
import NatCondominiumRepo from '../condominium/repository/NatCondominiumRepo'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons'
import {Box, Link, Table, TableRow, TableCell} from '@mui/material'
import SortableTableHeader, {
    SortableTableHeaderCell
} from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTableBody from '../../../styledComponents/adminPage/SortablePaginationTableBody'
import {formatDateTimeForUpdateDate} from '../../../formatters/formatDate'
import {PageFotterAreaLeft, PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate'
import {ADMINISTORATOR_MODE, DISASTER_RELEASE_SETTING} from '../../../CodeMaster'
import {AppAdminContext} from '../../../app/AppAdminContext'
import styled from 'styled-components'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import {BoldText} from './admin/NatAdminDisasterCommon'
import {BackLink} from '../../../styledComponents/adminPage/Links'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {Location} from 'react-router-dom'

const SELECTED_ADMINISTORATOR_MODE: string = 'selectedMode';

interface Props {
    condominiumCd: string
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    condominiumName: string
    disasterInfoList: DisasterInfo[]
    isDescSort: boolean
    isLoadCompleted: boolean
}

class NatAdminDisasterPreventionsPage extends Component<Props & AdminPagePropsSet, States> {
    mode: Nullable<string>;

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.mode = SessionStorageHelper.getStringItemOrNull(SELECTED_ADMINISTORATOR_MODE);
        this.state = {
            condominiumName: '',
            disasterInfoList: [],
            isDescSort: true,
            isLoadCompleted: false
        }
        this.handleSortDisasterInfo = this.handleSortDisasterInfo.bind(this)
        this.registerDisasterInfoLinkClick = this.registerDisasterInfoLinkClick.bind(this);
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumCd, condominiumRepo, disasterRepo, eventEmitter} = this.props

            // 全体モードの場合は物件名を表示するために物件情報を取得する
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                const condominiumResponse: Nullable<NatGetCondominium> = await this.props.asyncCallFunction(
                    condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (condominiumResponse?.details) {
                    this.setState({
                        condominiumName: condominiumResponse.details.condominiumName
                    })
                }
            }
            // 防災情報検索（管理）
            const response = await this.props.asyncCallFunction(
                disasterRepo.searchDisasterInfoList.bind(disasterRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response?.details) {
                this.setState({
                    disasterInfoList: response.details.disasterInfo
                })
            }
            this.showInitialMessage();
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage;
        this.props.navigate(this.props.location.pathname, {replace: true, state: {}});
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage);
    }

    static getReleaseSetting(releaseSettingCode: string): string {
        const releaseSetting = DISASTER_RELEASE_SETTING.codeToEnumConstants(releaseSettingCode)
        if (releaseSetting) {
            return releaseSetting.label
        } else {
            return ''
        }
    }

    handleSortDisasterInfo(): void {
        const {disasterInfoList, isDescSort} = this.state
        this.setState({
            disasterInfoList: disasterInfoList.slice().reverse(),
            isDescSort: !isDescSort
        })
    }

    registerDisasterInfoLinkClick(): void {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/disasters/preventionRegister`);
    }

    renderDisasterInfo(disasterInfoList: DisasterInfo[]): Nullable<ReactElement> {
        if (disasterInfoList.length === 0) {
            return null
        }

        const headers: SortableTableHeaderCell[] = [];
        headers.push(
            {id: 'title', label: 'タイトル', align: 'left'},
            {id: 'releaseSetting', label: '公開区分', align: 'left'},
            {id: 'updateUser', label: '更新者', align: 'left'},
            {
                id: 'updateDatetime',
                label: '更新日時',
                align: 'left',
                sortable: true,
                direction: this.state.isDescSort ? 'desc' : 'asc',
            }
        );
        return (
            <Table>
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortDisasterInfo}
                />
                <SortablePaginationTableBody>
                    {disasterInfoList.map(
                        recode => this.renderDisasterInfoRecord(recode),
                    )}
                </SortablePaginationTableBody>
            </Table>
        )
    }

    renderDisasterInfoRecord(recode: DisasterInfo): ReactElement {
        const disasterDetailUrl: string
            = `/admin/condominiums/${this.props.condominiumCd}/disasters/preventions/${recode.disasterPreventionId}`;
        return (
            <TableRow key={recode.disasterPreventionId}>
                <TableCellWordBreakAll style={{width: '612px'}}>
                    <Link href={disasterDetailUrl}>{recode.title}</Link>
                </TableCellWordBreakAll>
                <TableCell style={{width: '197px'}}>
                    {NatAdminDisasterPreventionsPage.getReleaseSetting(recode.releaseSetting)}
                </TableCell>
                <TableCellWordBreakAll style={{width: '233px'}}>
                    {recode.modifierUserFamilyName + ' ' + recode.modifierUserFirstName}
                </TableCellWordBreakAll>
                <TableCell style={{width: '234px'}}>
                    {formatDateTimeForUpdateDate(recode.updateDatetime)}
                </TableCell>
            </TableRow>
        )
    }

    render(): Nullable<ReactElement> {
        const {condominiumName, disasterInfoList} = this.state

        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <Box height={'10px'}/>
                {this.mode === ADMINISTORATOR_MODE.ALL.value &&
                    <>
                        <DisplayFlex>
                            物件名
                            <BoldText>{condominiumName}</BoldText>
                        </DisplayFlex>
                        <Box height={'16px'}/>
                    </>
                }
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_DISASTER_PREVENTION}>
                    <AdminDefaultMediumButton onClick={this.registerDisasterInfoLinkClick}>
                        新規
                    </AdminDefaultMediumButton>
                    <Box height={'16px'}/>
                </AuthorityChecker>
                {disasterInfoList.length !== 0
                    ? <PageMainArea>{this.renderDisasterInfo(disasterInfoList)}</PageMainArea>
                    : <div>
                        防災情報が登録されていません。
                        <Box height={'14px'}/>
                    </div>
                }
                {this.mode === ADMINISTORATOR_MODE.ALL.value &&
                    <>
                        <Box height={'10px'}/>
                        <PageFotterAreaLeft>
                            <BackLink
                                to={`/admin/disasters/mode/statuses`}
                            >
                                モード設定状況一覧へ戻る
                            </BackLink>
                        </PageFotterAreaLeft>
                    </>
                }
            </>
        )
    }
}

NatAdminDisasterPreventionsPage.contextType = AppAdminContext
export default createAdminPage(NatAdminDisasterPreventionsPage)

const TableCellWordBreakAll = styled(TableCell)`
    &.MuiTableCell-root {
        word-break: break-all;
    }
`

const DisplayFlex: StyledDiv = styled.div`
    display: flex;
    gap: 16px;
`
